import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { FilterState, formatDateToISO } from "@/features/filters/filter.types";
import {
  filtersAreEqual as compareFilters,
  isComparisonEnabled,
} from "@/features/filters/filter.utils";

import {
  getAnalyticsOverviewApiV1AnalyticsOverviewGet,
  getSalesFunnelApiV1SalesFunnelCardGet,
  getMetaAdsManagerTotalApiV1MetaAdsManagerTotalGet,
  getGoogleAdsManagerTotalApiV1GoogleAdsManagerTotalGet,
} from "@/backend-sdk/client/sdk.gen";

import type {
  AnalyticsOverviewResponse,
  SalesFunnelCardResponse,
  MetaAdsManagerTotalResponse,
  GoogleAdsManagerTotalResponse,
} from "@/backend-sdk/client/types.gen";
import { useAccount } from "@/contexts/account.context";
import AnalyticsOverviewSkeleton from "./AnalyticsOverviewCard";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import { KpiNumber } from "@/components/ui/kpi-number";
import {
  CarregandoSobreposto,
  CarregandoValor,
  EstadoDoValor,
  ValorIndisponivel,
} from "@/components/ui/estado-do-valor";
import {
  MIDIA_PAGA,
  type RequisitoDeFonte,
} from "@/lib/motivo-do-indisponivel";
import {
  ActiveFiltersDisplay,
  DrilldownProvider,
  useDrilldown,
  DrillDimension,
} from "@/features/drilldown";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";

// Importações dos componentes refatorados
import VendasDispositivosCard from "./VendasDispositivosCard";
import VendasEstadoCard from "./VendasEstadoCard";
import PesquisaInternaTable from "./PesquisaInternaTable";
import ControleDiarioTable from "./ControleDiarioTable";
import AnaliseOrigemTable from "./AnaliseOrigemTable";
import CategoriasMaisVendidasTable from "./CategoriasMaisVendidasTable";
import ProdutosMaisVendidosTable from "./ProdutosMaisVendidosTable";

// Importar utilitários de formatação centralizados
import {
  formatCurrency as formatCurrencyUtil,
  formatPercent,
  formatDecimal,
  formatSignedPercent,
} from "@/utils/numberFormat";

/**
 * A variação de um KPI do funil: `▴ +12,3%` / `▾ −12,3%`.
 *
 * A seta continua desenhada, mas o sinal agora vive no número — item 34 da auditoria, que
 * apontou o percentual saindo em módulo e a direção ficando só por conta da cor e da seta.
 */
const variacaoDoFunil = (valor: number | null | undefined): string =>
  `${Number(valor ?? 0) >= 0 ? "▴" : "▾"} ${formatSignedPercent(Number(valor ?? 0), 1)}`;

type KpiTrend = "up" | "down" | "neutral";

/**
 * A seta de uma variação que a resposta publica sem tendência pronta — a mesma regra do
 * pde-api-server (M-102): zero não sobe nem desce.
 */
const tendenciaDaVariacao = (variacao: number | null | undefined): KpiTrend => {
  if (variacao === null || variacao === undefined || Number.isNaN(variacao)) {
    return "neutral";
  }
  if (variacao > 0) return "up";
  if (variacao < 0) return "down";
  return "neutral";
};

const toKpiTrend = (trend: string | null | undefined): KpiTrend => {
  if (trend === "up" || trend === "down" || trend === "neutral") {
    return trend;
  }
  return "neutral";
};

// Função helper para formatar números com segurança
const formatNumber = (value: unknown, decimals: number = 2): string => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "0";
  }
  return formatDecimal(Number(value), decimals);
};

// Função helper para formatar moeda com segurança
const formatCurrency = (
  value: unknown,
  currencySymbol: string = "R$",
): string => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "N/D";
  }
  return formatCurrencyUtil(Number(value));
};

// Função helper para formatar percentual com segurança
const formatPercentage = (
  value: unknown,
  percentageSymbol: string = "%",
): string => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "N/D";
  }
  return formatPercent(Number(value));
};

/** O que cada número da Visão Geral precisa para existir. */
const FONTES: Readonly<
  Record<
    "ga4" | "meta" | "google" | "investimento" | "ga4ComMidia",
    readonly RequisitoDeFonte[]
  >
> = {
  ga4: ["ga4"],
  meta: ["meta_ads"],
  google: ["google_ads"],
  investimento: [MIDIA_PAGA],
  // Receita do GA4 cruzada com o investimento das mídias: ROAS, CPS, Connect Rate.
  ga4ComMidia: ["ga4", MIDIA_PAGA],
};

/** N/D com o motivo; `sobreImagem` clareia o "!" para o fundo escuro do funil. */
const renderND = (
  fontes: readonly RequisitoDeFonte[],
  sobreImagem = false,
  erro = false,
) => (
  <ValorIndisponivel
    fontes={fontes}
    erro={erro}
    iconeClassName={sobreImagem ? "text-white/80 hover:text-white" : undefined}
  />
);

/** Card cujo gerenciador não respondeu — antes, o esqueleto ficava na tela para sempre. */
const CardSemResposta = ({ fonte }: { fonte: string }) => (
  <div className="text-center py-8">
    <p className="text-sm text-muted-foreground mb-1">
      Não conseguimos carregar os dados de {fonte}.
    </p>
    <p className="text-xs text-muted-foreground">
      Tente atualizar a página em alguns instantes.
    </p>
  </div>
);

// Componente para subcard - design mais compacto e clean
const SubCard = ({
  title,
  value,
  change,
  trend,
  isCurrency = false,
  isPercentage = false,
  isDecimal = false,
  projection,
  currencySymbol = "R$",
  percentageSymbol = "%",
  fontes,
}: {
  title: string;
  fontes: readonly RequisitoDeFonte[];
  value: number;
  change: number;
  trend: "up" | "down" | string;
  isCurrency?: boolean;
  isPercentage?: boolean;
  isDecimal?: boolean;
  projection?: number;
  currencySymbol?: string;
  percentageSymbol?: string;
}) => {
  const { filter } = useGlobalFilter();
  const comparisonEnabled = isComparisonEnabled(filter);

  return (
    <div className="metric-card group">
      <p className="metric-label mb-0.5 flex items-center gap-1">
        <span className="truncate">{title.toLocaleUpperCase("pt-BR")}</span>
        <HelpTooltip content={getKpiTooltip(title)} />
      </p>
      <div className="metric-value min-w-0">
        {(() => {
          const empty =
            value === null || value === undefined || isNaN(Number(value));
          if (empty) return <ValorIndisponivel fontes={fontes} />;

          const numericValue = Number(value);
          const formatFn = isCurrency
            ? (n: number) => formatCurrency(n, currencySymbol)
            : isPercentage
              ? (n: number) => formatPercentage(n, percentageSymbol)
              : isDecimal
                ? (n: number) =>
                    n.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                : (n: number) =>
                    new Intl.NumberFormat("pt-BR").format(Math.trunc(n));

          return (
            <EstadoDoValor carregando={false} disponivel fontes={fontes}>
              <KpiNumber
                value={numericValue}
                format={formatFn}
                className="font-light text-foreground"
                fontPx={18}
                ariaLabel={title}
                showComparison={comparisonEnabled}
                changePercentage={change}
                trendIndicator={
                  trend === "up" ? "up" : trend === "down" ? "down" : "neutral"
                }
              />
            </EstadoDoValor>
          );
        })()}
      </div>
      {isCurrency &&
        projection !== undefined &&
        projection !== null &&
        !isNaN(Number(projection)) && (
          <div className="text-2xs text-primary/80 font-medium mt-0.5">
            Projeção: {formatCurrency(projection, currencySymbol)}
          </div>
        )}
    </div>
  );
};

// Conteúdo interno da página (precisa estar dentro do DrilldownProvider)
const UnifiedOverviewContent: React.FC = () => {
  const { selectedAccount } = useAccount();
  const { filter: globalFilter } = useGlobalFilter();
  const { apiFilters, apiFiltersKey } = useDrilldown();
  const [filterState, setFilterState] = useState<FilterState | null>(null);
  const [analyticsOverviewData, setAnalyticsOverviewData] =
    useState<AnalyticsOverviewResponse | null>(null);
  const [salesFunnelData, setSalesFunnelData] =
    useState<SalesFunnelCardResponse | null>(null);
  const [metaManagerData, setMetaManagerData] =
    useState<MetaAdsManagerTotalResponse | null>(null);
  const [googleAdsManagerData, setGoogleAdsManagerData] =
    useState<GoogleAdsManagerTotalResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Falha por bloco. A do funil e as dos dois gerenciadores só iam para o console, e a tela
  // ficava com N/D (funil) ou esqueleto (cards) para sempre, sem dizer que houve erro.
  const [erroFunil, setErroFunil] = useState(false);
  const [erroMeta, setErroMeta] = useState(false);
  const [erroGoogle, setErroGoogle] = useState(false);
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();
  const lastFilterRef = useRef<FilterState | null>(null);
  const lastAnalyticsParamsRef = useRef<{
    startDate: string;
    endDate: string;
    period: string;
    comparison?: string;
    crossFiltersKey: string;
    campaignFiltersNonce: number;
  } | null>(null);
  const lastSalesFunnelParamsRef = useRef<{
    startDate: string;
    endDate: string;
    period: string;
    comparison?: string;
    crossFiltersKey: string;
    campaignFiltersNonce: number;
  } | null>(null);

  // Obter filtros de cross-filtering para passar às APIs
  const crossFilters = apiFilters;
  const customerUuid =
    selectedAccount?.customer_uuid ||
    selectedAccount?.id ||
    localStorage.getItem("active_customer_uuid") ||
    null;

  /**
   * Um número do funil sem resposta: indicador enquanto a busca está em voo, e N/D com o motivo
   * quando ela terminou sem dado. Antes as duas situações desenhavam o mesmo N/D.
   */
  const funilAusente = (
    fontes: readonly RequisitoDeFonte[],
    sobreImagem = false,
  ) =>
    loading || (!filterState && !erroFunil) ? (
      <CarregandoValor className={sobreImagem ? "text-white" : undefined} />
    ) : (
      renderND(fontes, sobreImagem, erroFunil)
    );

  // Função para buscar dados do novo endpoint Analytics Overview
  const fetchAnalyticsOverviewData = useCallback(
    async (filter: FilterState) => {
      try {
        // Formatar datas para o formato esperado pela API (YYYY-MM-DD)
        const startDate = formatDateToISO(filter.dateRange.startDate);
        const endDate = formatDateToISO(filter.dateRange.endDate);

        // Evitar chamadas duplicadas - incluir comparison para detectar mudanças de comparação
        const paramsSignature = {
          startDate,
          endDate,
          period: filter.period,
          comparison: filter.comparison?.type,
          crossFiltersKey: apiFiltersKey,
          campaignFiltersNonce,
        };
        if (
          lastAnalyticsParamsRef.current &&
          lastAnalyticsParamsRef.current.startDate ===
            paramsSignature.startDate &&
          lastAnalyticsParamsRef.current.endDate === paramsSignature.endDate &&
          lastAnalyticsParamsRef.current.period === paramsSignature.period &&
          lastAnalyticsParamsRef.current.comparison ===
            paramsSignature.comparison &&
          lastAnalyticsParamsRef.current.crossFiltersKey ===
            paramsSignature.crossFiltersKey &&
          lastAnalyticsParamsRef.current.campaignFiltersNonce ===
            paramsSignature.campaignFiltersNonce
        ) {
          return;
        }

        const customerUuid =
          selectedAccount?.customer_uuid ||
          selectedAccount?.id ||
          localStorage.getItem("active_customer_uuid") ||
          undefined;
        if (!customerUuid) return;

        const { data } =
          await getAnalyticsOverviewApiV1AnalyticsOverviewGet<true>({
            query: {
              start_date: startDate,
              end_date: endDate,
              period: filter.period,
              comparison_type: filter.comparison?.type,
              comparison_label: filter.comparison?.label,
              custom_start_date:
                filter.period === "custom" && filter.customDateRange?.startDate
                  ? formatDateToISO(filter.customDateRange.startDate)
                  : undefined,
              custom_end_date:
                filter.period === "custom" && filter.customDateRange?.endDate
                  ? formatDateToISO(filter.customDateRange.endDate)
                  : undefined,
              customer_uuid: customerUuid,
            },
          });
        setAnalyticsOverviewData(data);
        lastAnalyticsParamsRef.current = paramsSignature;
      } catch (err) {
        console.error("Erro ao buscar dados do Analytics Overview:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Erro ao carregar dados do Analytics Overview",
        );
      }
    },
    [selectedAccount, apiFiltersKey, campaignFiltersNonce],
  );

  // Função para buscar dados do Sales Funnel
  const fetchSalesFunnelData = useCallback(
    async (filter: FilterState) => {
      try {
        // Formatar datas para o formato esperado pela API (YYYY-MM-DD)
        const startDate = formatDateToISO(filter.dateRange.startDate);
        const endDate = formatDateToISO(filter.dateRange.endDate);

        // Evitar chamadas duplicadas - incluir comparison para detectar mudanças de comparação
        const paramsSignature = {
          startDate,
          endDate,
          period: filter.period,
          comparison: filter.comparison?.type,
          crossFiltersKey: apiFiltersKey,
          campaignFiltersNonce,
        };
        if (
          lastSalesFunnelParamsRef.current &&
          lastSalesFunnelParamsRef.current.startDate ===
            paramsSignature.startDate &&
          lastSalesFunnelParamsRef.current.endDate ===
            paramsSignature.endDate &&
          lastSalesFunnelParamsRef.current.period === paramsSignature.period &&
          lastSalesFunnelParamsRef.current.comparison ===
            paramsSignature.comparison &&
          lastSalesFunnelParamsRef.current.crossFiltersKey ===
            paramsSignature.crossFiltersKey &&
          lastSalesFunnelParamsRef.current.campaignFiltersNonce ===
            paramsSignature.campaignFiltersNonce
        ) {
          return;
        }

        const customerUuid =
          selectedAccount?.customer_uuid ||
          selectedAccount?.id ||
          localStorage.getItem("active_customer_uuid") ||
          undefined;
        if (!customerUuid) return;

        const { data } = await getSalesFunnelApiV1SalesFunnelCardGet<true>({
          query: {
            start_date: startDate,
            end_date: endDate,
            period: filter.period,
            comparison: filter.comparison?.type,
            comparison_label: filter.comparison?.label,
            custom_start_date:
              filter.period === "custom" && filter.customDateRange?.startDate
                ? formatDateToISO(filter.customDateRange.startDate)
                : undefined,
            custom_end_date:
              filter.period === "custom" && filter.customDateRange?.endDate
                ? formatDateToISO(filter.customDateRange.endDate)
                : undefined,
            customer_uuid: customerUuid,
          },
        });
        setSalesFunnelData(data);
        setErroFunil(false);
        lastSalesFunnelParamsRef.current = paramsSignature;
      } catch (err) {
        console.error("Erro ao buscar dados do Sales Funnel:", err);
        setErroFunil(true);
        setError(
          err instanceof Error
            ? err.message
            : "Erro ao carregar dados do Sales Funnel",
        );
      }
    },
    [selectedAccount, apiFiltersKey, campaignFiltersNonce],
  );

  const fetchMetaManagerData = useCallback(
    async (filter: FilterState) => {
      try {
        const startDate = formatDateToISO(filter.dateRange.startDate);
        const endDate = formatDateToISO(filter.dateRange.endDate);
        const customerUuid =
          selectedAccount?.customer_uuid ||
          selectedAccount?.id ||
          localStorage.getItem("active_customer_uuid") ||
          undefined;
        if (!customerUuid) return;

        const { data } =
          await getMetaAdsManagerTotalApiV1MetaAdsManagerTotalGet<true>({
            query: {
              customer_uuid: customerUuid,
              start_date: startDate,
              end_date: endDate,
              comparison: filter.comparison?.type || "previous_period",
            },
          });
        setMetaManagerData(data);
        setErroMeta(false);
      } catch (err) {
        console.error("Erro ao buscar dados do Meta Ads Manager Total:", err);
        setErroMeta(true);
      }
    },
    [selectedAccount],
  );

  const fetchGoogleAdsManagerData = useCallback(
    async (filter: FilterState) => {
      try {
        const startDate = formatDateToISO(filter.dateRange.startDate);
        const endDate = formatDateToISO(filter.dateRange.endDate);
        const customerUuid =
          selectedAccount?.customer_uuid ||
          selectedAccount?.id ||
          localStorage.getItem("active_customer_uuid") ||
          undefined;
        if (!customerUuid) return;

        const { data } =
          await getGoogleAdsManagerTotalApiV1GoogleAdsManagerTotalGet<true>({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison: filter.comparison?.type || "previous_period",
              customer_uuid: customerUuid,
            },
          });
        setGoogleAdsManagerData(data);
        setErroGoogle(false);
      } catch (err) {
        console.error("Erro ao buscar dados do Google Ads Manager Total:", err);
        setErroGoogle(true);
      }
    },
    [selectedAccount],
  );

  // Função para buscar dados do Google Analytics - agora usa os endpoints Analytics Overview e Sales Funnel
  const fetchGoogleAnalyticsData = useCallback(
    async (filter: FilterState) => {
      // Delegar para as funções específicas
      await Promise.all([
        fetchAnalyticsOverviewData(filter),
        fetchSalesFunnelData(filter),
        fetchMetaManagerData(filter),
        fetchGoogleAdsManagerData(filter),
      ]);
    },
    [
      fetchAnalyticsOverviewData,
      fetchSalesFunnelData,
      fetchMetaManagerData,
      fetchGoogleAdsManagerData,
    ],
  );

  useEffect(() => {
    if (!globalFilter) {
      return;
    }

    if (compareFilters(lastFilterRef.current, globalFilter)) {
      return;
    }

    lastFilterRef.current = globalFilter;
    setFilterState(globalFilter);
  }, [globalFilter]);

  // Fallback: se filtros não inicializaram por algum motivo, cria estado padrão e dispara carregamento
  useEffect(() => {
    if (!filterState && globalFilter) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilterState(globalFilter);
    }
  }, [filterState, globalFilter]);

  // Carregar dados quando filtros mudarem
  useEffect(() => {
    if (filterState) {
      // Ativa estado de carregamento SEM zerar os dados (evita "pisca-pisca")
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(true);

      fetchGoogleAnalyticsData(filterState)
        .catch((err) => {
          console.error("Erro ao carregar dados do dashboard:", err);
        })
        .finally(() => {
          // Desativa skeletons somente após todas as requisições concluírem
          setLoading(false);
        });
    }
  }, [
    filterState,
    fetchGoogleAnalyticsData,
    apiFiltersKey,
    campaignFiltersNonce,
  ]);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Visão Geral</h1>
        <p className="page-description">
          Dashboard consolidado de performance e marketing
        </p>
      </div>

      <GlobalFilter
        rightSlot={
          <CampaignFiltersControl
            customerUuid={customerUuid}
            accountId={selectedAccount?.account_id}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />

      {/* Barra de filtros ativos (Cross-filtering) */}
      <ActiveFiltersDisplay emptyText="Clique em uma linha para filtrar a página inteira" />

      {/* PRIMEIRA LINHA: 3 CONTAINERS PRINCIPAIS */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* 1º Container: Loja Virtual Analytics */}
        <Card className="hover-lift">
          <CardHeader>
            <CardTitle
              icon={
                <img
                  src="/analytics.png"
                  alt="Analytics"
                  className="w-5 h-5 object-contain"
                />
              }
              className="cursor-pointer hover:text-primary transition-colors"
              onClick={() => (window.location.href = "/analytics")}
            >
              Loja Virtual (Analytics)
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            {!analyticsOverviewData ? (
              error && !loading ? (
                <CardSemResposta fonte="Google Analytics" />
              ) : (
                <AnalyticsOverviewSkeleton />
              )
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-500 mb-2">Erro ao carregar dados</p>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            ) : analyticsOverviewData ? (
              // Dados do novo endpoint Analytics Overview
              <>
                {/* Overlay de loading (mantém conteúdo para transição suave) */}
                {loading && <CarregandoSobreposto visivel />}
                {/* Primeira linha: 2 cards principais */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="metric-card">
                    <p className="metric-label flex items-center gap-1">
                      <span className="truncate">Receita Total</span>
                      <HelpTooltip content={getKpiTooltip("Receita Total")} />
                    </p>
                    <div className="min-w-0">
                      {
                        <EstadoDoValor
                          carregando={false}
                          disponivel={Number.isFinite(
                            Number(analyticsOverviewData.total_revenue),
                          )}
                          fontes={FONTES.ga4}
                        >
                          <KpiNumber
                            value={Number(analyticsOverviewData.total_revenue)}
                            format={(n) =>
                              formatCurrency(
                                n,
                                analyticsOverviewData.currency_symbol,
                              )
                            }
                            className="metric-value"
                            fontPx={18}
                            ariaLabel="Receita total"
                            showComparison={isComparisonEnabled(globalFilter)}
                            changePercentage={
                              analyticsOverviewData.changes.revenue
                            }
                            trendIndicator={tendenciaDaVariacao(
                              analyticsOverviewData.changes.revenue,
                            )}
                          />
                        </EstadoDoValor>
                      }
                    </div>
                    <div className="text-2xs text-primary/80 font-medium mt-0.5">
                      Projeção:{" "}
                      {formatCurrency(
                        analyticsOverviewData.revenue_projection,
                        analyticsOverviewData.currency_symbol,
                      )}
                    </div>
                  </div>
                  <div className="metric-card">
                    <p className="metric-label flex items-center gap-1">
                      <span className="truncate">Investimento</span>
                      <HelpTooltip content={getKpiTooltip("Investimento")} />
                    </p>
                    <div className="min-w-0">
                      {
                        <EstadoDoValor
                          carregando={false}
                          disponivel={Number.isFinite(
                            Number(analyticsOverviewData.total_investment),
                          )}
                          fontes={FONTES.investimento}
                        >
                          <KpiNumber
                            value={Number(
                              analyticsOverviewData.total_investment,
                            )}
                            format={(n) =>
                              formatCurrency(
                                n,
                                analyticsOverviewData.currency_symbol,
                              )
                            }
                            className="metric-value"
                            fontPx={18}
                            ariaLabel="Investimento"
                            showComparison={isComparisonEnabled(globalFilter)}
                            changePercentage={
                              analyticsOverviewData.changes.investment
                            }
                            trendIndicator={tendenciaDaVariacao(
                              analyticsOverviewData.changes.investment,
                            )}
                          />
                        </EstadoDoValor>
                      }
                    </div>
                    <div className="text-2xs text-primary/80 font-medium mt-0.5">
                      Projeção:{" "}
                      {formatCurrency(
                        analyticsOverviewData.investment_projection,
                        analyticsOverviewData.currency_symbol,
                      )}
                    </div>
                  </div>
                </div>

                {/* Linhas seguintes: 3 cards por linha */}
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <SubCard
                    title="Ticket Médio"
                    fontes={FONTES.ga4}
                    value={analyticsOverviewData.average_order_value}
                    change={analyticsOverviewData.changes.average_order_value}
                    trend={tendenciaDaVariacao(
                      analyticsOverviewData.changes.average_order_value,
                    )}
                    isCurrency={true}
                    currencySymbol={analyticsOverviewData.currency_symbol}
                  />
                  <SubCard
                    title="Taxa Conversão"
                    fontes={FONTES.ga4}
                    value={analyticsOverviewData.conversion_rate}
                    change={analyticsOverviewData.changes.conversion_rate}
                    trend={tendenciaDaVariacao(
                      analyticsOverviewData.changes.conversion_rate,
                    )}
                    isPercentage={true}
                    percentageSymbol={analyticsOverviewData.percentage_symbol}
                  />
                  <SubCard
                    title="Sessões"
                    fontes={FONTES.ga4}
                    value={analyticsOverviewData.total_sessions}
                    change={analyticsOverviewData.changes.sessions}
                    trend={tendenciaDaVariacao(
                      analyticsOverviewData.changes.sessions,
                    )}
                  />
                  <SubCard
                    title="Pedidos"
                    fontes={FONTES.ga4}
                    value={analyticsOverviewData.total_transactions}
                    change={analyticsOverviewData.changes.transactions}
                    trend={tendenciaDaVariacao(
                      analyticsOverviewData.changes.transactions,
                    )}
                  />
                  <SubCard
                    title="ROAS"
                    fontes={FONTES.ga4ComMidia}
                    value={analyticsOverviewData.roas}
                    change={analyticsOverviewData.changes.roas}
                    trend={tendenciaDaVariacao(
                      analyticsOverviewData.changes.roas,
                    )}
                    isDecimal={true}
                  />
                  <SubCard
                    title="% Marketing"
                    fontes={FONTES.ga4ComMidia}
                    value={analyticsOverviewData.marketing_percentage}
                    change={analyticsOverviewData.changes.marketing_percentage}
                    trend={tendenciaDaVariacao(
                      analyticsOverviewData.changes.marketing_percentage,
                    )}
                    isPercentage={true}
                    percentageSymbol={analyticsOverviewData.percentage_symbol}
                  />
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-2">
                  Nenhum dado encontrado para o período selecionado
                </p>
                <p className="text-sm text-muted-foreground">
                  Verifique se há dados disponíveis ou ajuste os filtros
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2º Container: Meta Ads Gerenciador */}
        <Card className="hover-lift">
          <CardHeader>
            <CardTitle
              icon={
                <img
                  src="/metaAds.png"
                  alt="Meta Ads"
                  className="w-5 h-5 object-contain"
                />
              }
              className="cursor-pointer hover:text-primary transition-colors"
              onClick={() => (window.location.href = "/meta-ads")}
            >
              Meta Ads (Total)
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            {loading && metaManagerData && <CarregandoSobreposto visivel />}
            {!metaManagerData ? (
              erroMeta && !loading ? (
                <CardSemResposta fonte="Meta Ads" />
              ) : (
                <AnalyticsOverviewSkeleton />
              )
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="metric-card">
                    <p className="metric-label flex items-center gap-1">
                      <span className="truncate">Receita (META)</span>
                      <HelpTooltip content={getKpiTooltip("Receita (META)")} />
                    </p>
                    <div className="min-w-0">
                      {
                        <EstadoDoValor
                          carregando={false}
                          disponivel={Number.isFinite(
                            Number(metaManagerData.revenue),
                          )}
                          fontes={FONTES.meta}
                        >
                          <KpiNumber
                            value={Number(metaManagerData.revenue)}
                            format={(n) =>
                              formatCurrency(n, metaManagerData.currency_symbol)
                            }
                            className="metric-value"
                            fontPx={18}
                            ariaLabel="Receita (Meta)"
                            showComparison={isComparisonEnabled(globalFilter)}
                            changePercentage={
                              metaManagerData.revenue_change_percentage
                            }
                            trendIndicator={
                              metaManagerData.revenue_trend_indicator === "up"
                                ? "up"
                                : metaManagerData.revenue_trend_indicator ===
                                    "down"
                                  ? "down"
                                  : "neutral"
                            }
                          />
                        </EstadoDoValor>
                      }
                    </div>
                    <div className="text-2xs text-primary/80 font-medium mt-0.5">
                      Projeção:{" "}
                      {formatCurrency(
                        metaManagerData.revenue_projection,
                        metaManagerData.currency_symbol,
                      )}
                    </div>
                  </div>
                  <SubCard
                    title="Investimento"
                    fontes={FONTES.meta}
                    value={metaManagerData.investment}
                    change={metaManagerData.investment_change_percentage}
                    trend={toKpiTrend(
                      metaManagerData.investment_trend_indicator,
                    )}
                    isCurrency
                    projection={metaManagerData.investment_projection}
                    currencySymbol={metaManagerData.currency_symbol}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <SubCard
                    title="CPM"
                    fontes={FONTES.meta}
                    value={metaManagerData.cpm}
                    change={metaManagerData.cpm_change_percentage}
                    trend={toKpiTrend(metaManagerData.cpm_trend_indicator)}
                    isCurrency
                    currencySymbol={metaManagerData.currency_symbol}
                  />
                  <SubCard
                    title="CTR (Cliques Link)"
                    fontes={FONTES.meta}
                    value={metaManagerData.ctr_link}
                    change={metaManagerData.ctr_link_change_percentage}
                    trend={toKpiTrend(metaManagerData.ctr_link_trend_indicator)}
                    isPercentage
                    percentageSymbol={metaManagerData.percentage_symbol}
                  />
                  <SubCard
                    title="CPC (Cliques Link)"
                    fontes={FONTES.meta}
                    value={metaManagerData.cpc_link}
                    change={metaManagerData.cpc_link_change_percentage}
                    trend={toKpiTrend(metaManagerData.cpc_link_trend_indicator)}
                    isCurrency
                    currencySymbol={metaManagerData.currency_symbol}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <SubCard
                    title="CPA"
                    fontes={FONTES.meta}
                    value={metaManagerData.cpa}
                    change={metaManagerData.cpa_change_percentage}
                    trend={toKpiTrend(metaManagerData.cpa_trend_indicator)}
                    isCurrency
                    currencySymbol={metaManagerData.currency_symbol}
                  />
                  <SubCard
                    title="ROAS"
                    fontes={FONTES.meta}
                    value={metaManagerData.roas}
                    change={metaManagerData.roas_change_percentage}
                    trend={toKpiTrend(metaManagerData.roas_trend_indicator)}
                    isDecimal={true}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* 3º Container: Google Ads Gerenciador */}
        <Card className="hover-lift">
          <CardHeader>
            <CardTitle
              icon={
                <img
                  src="/googleAds.png"
                  alt="Google Ads"
                  className="w-5 h-5 object-contain"
                />
              }
              className="cursor-pointer hover:text-primary transition-colors"
              onClick={() => (window.location.href = "/google-ads")}
            >
              Google Ads
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            {loading && googleAdsManagerData && (
              <CarregandoSobreposto visivel />
            )}
            {erroGoogle && !loading && !googleAdsManagerData ? (
              <CardSemResposta fonte="Google Ads" />
            ) : (
              <>
                {/* Primeira linha: 2 cards principais */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {googleAdsManagerData ? (
                    <>
                      <div className="metric-card">
                        <p className="metric-label flex items-center gap-1">
                          <span className="truncate">Receita (Google)</span>
                          <HelpTooltip
                            content={getKpiTooltip("Receita (Google)")}
                          />
                        </p>
                        <div className="min-w-0">
                          {
                            <EstadoDoValor
                              carregando={false}
                              disponivel={Number.isFinite(
                                Number(googleAdsManagerData.revenue),
                              )}
                              fontes={FONTES.google}
                            >
                              <KpiNumber
                                value={Number(googleAdsManagerData.revenue)}
                                format={(n) => formatCurrency(n)}
                                className="metric-value"
                                fontPx={18}
                                ariaLabel="Receita (Google)"
                                showComparison={isComparisonEnabled(
                                  globalFilter,
                                )}
                                changePercentage={
                                  googleAdsManagerData.revenue_change_percentage
                                }
                                trendIndicator={
                                  googleAdsManagerData.revenue_trend_indicator ===
                                  "up"
                                    ? "up"
                                    : googleAdsManagerData.revenue_trend_indicator ===
                                        "down"
                                      ? "down"
                                      : "neutral"
                                }
                              />
                            </EstadoDoValor>
                          }
                        </div>
                        <div className="text-2xs text-primary/80 font-medium mt-0.5">
                          Projeção:{" "}
                          {formatCurrency(
                            googleAdsManagerData.revenue_projection,
                          )}
                        </div>
                      </div>
                      <SubCard
                        title="Investimento"
                        fontes={FONTES.google}
                        value={googleAdsManagerData.investment}
                        change={
                          googleAdsManagerData.investment_change_percentage
                        }
                        trend={toKpiTrend(
                          googleAdsManagerData.investment_trend_indicator,
                        )}
                        isCurrency
                        projection={googleAdsManagerData.investment_projection}
                        currencySymbol={googleAdsManagerData.currency_symbol}
                      />
                    </>
                  ) : (
                    <>
                      <div className="metric-card">
                        <Skeleton className="h-4 w-24 mb-2" />
                        <Skeleton className="h-8 w-32 mb-2" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                      <div className="metric-card">
                        <Skeleton className="h-4 w-20 mb-2" />
                        <Skeleton className="h-6 w-16 mb-2" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </>
                  )}
                </div>

                {/* Linhas seguintes: 3 cards por linha */}
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {googleAdsManagerData ? (
                    <>
                      <SubCard
                        title="Impressões"
                        fontes={FONTES.google}
                        value={googleAdsManagerData.impressions}
                        change={
                          googleAdsManagerData.impressions_change_percentage
                        }
                        trend={toKpiTrend(
                          googleAdsManagerData.impressions_trend_indicator,
                        )}
                      />
                      <SubCard
                        title="CTR"
                        fontes={FONTES.google}
                        value={googleAdsManagerData.ctr}
                        change={googleAdsManagerData.ctr_change_percentage}
                        trend={toKpiTrend(
                          googleAdsManagerData.ctr_trend_indicator,
                        )}
                        isPercentage
                      />
                      <SubCard
                        title="CPC Médio"
                        fontes={FONTES.google}
                        value={googleAdsManagerData.cpc}
                        change={googleAdsManagerData.cpc_change_percentage}
                        trend={toKpiTrend(
                          googleAdsManagerData.cpc_trend_indicator,
                        )}
                        isCurrency
                        currencySymbol={googleAdsManagerData.currency_symbol}
                      />
                    </>
                  ) : (
                    <>
                      <div className="p-3 border border-border rounded-lg">
                        <Skeleton className="h-3 w-16 mb-2" />
                        <Skeleton className="h-6 w-12" />
                      </div>
                      <div className="p-3 border border-border rounded-lg">
                        <Skeleton className="h-3 w-8 mb-2" />
                        <Skeleton className="h-6 w-10" />
                      </div>
                      <div className="p-3 border border-border rounded-lg">
                        <Skeleton className="h-3 w-16 mb-2" />
                        <Skeleton className="h-6 w-12" />
                      </div>
                    </>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {googleAdsManagerData ? (
                    <>
                      <SubCard
                        title="CPA"
                        fontes={FONTES.google}
                        value={googleAdsManagerData.cpa}
                        change={googleAdsManagerData.cpa_change_percentage}
                        trend={toKpiTrend(
                          googleAdsManagerData.cpa_trend_indicator,
                        )}
                        isCurrency
                        currencySymbol={googleAdsManagerData.currency_symbol}
                      />
                      <SubCard
                        title="ROAS"
                        fontes={FONTES.google}
                        value={googleAdsManagerData.roas}
                        change={googleAdsManagerData.roas_change_percentage}
                        trend={toKpiTrend(
                          googleAdsManagerData.roas_trend_indicator,
                        )}
                        isDecimal={true}
                      />
                    </>
                  ) : (
                    <>
                      <div className="p-3 border border-border rounded-lg">
                        <Skeleton className="h-3 w-12 mb-2" />
                        <Skeleton className="h-6 w-12" />
                      </div>
                      <div className="p-3 border border-border rounded-lg">
                        <Skeleton className="h-3 w-12 mb-2" />
                        <Skeleton className="h-6 w-10" />
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SEGUNDA LINHA: FUNIL COM IMAGEM DE FUNDO */}
      <Card className="hover:shadow-md transition-shadow bg-card border">
        <CardHeader>
          <CardTitle subtitle="Análise completa do fluxo de conversão">
            FUNIL DE VENDAS
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Imagem do Funil 3D com texto sobreposto (col-span-5) */}
            <div className="lg:col-span-5 flex items-center justify-center relative">
              <img
                src="/cone3d5.png"
                alt="Funil de Vendas 3D"
                className="w-full h-auto max-h-[600px] object-contain"
              />

              {/* Texto sobreposto nas fases do funil */}
              <div className="absolute inset-0">
                {/* Fase 1 - Sessões */}
                <div
                  className="absolute text-center text-white"
                  style={{
                    top: "14%",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  <div className="text-sm font-medium opacity-90">Sessões</div>
                  <div className="text-2xl font-semibold leading-tight">
                    {salesFunnelData
                      ? salesFunnelData.sessions === 0
                        ? renderND(FONTES.ga4, true)
                        : salesFunnelData.sessions.toLocaleString("pt-BR")
                      : funilAusente(FONTES.ga4, true)}
                  </div>
                  <div className="text-xs opacity-70">
                    {salesFunnelData &&
                    typeof salesFunnelData.changes?.sessions === "number"
                      ? variacaoDoFunil(salesFunnelData.changes.sessions)
                      : null}
                  </div>
                </div>

                {/* Fase 2 - Carrinhos */}
                <div
                  className="absolute text-center text-white"
                  style={{
                    top: "33%",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  <div className="text-sm font-medium opacity-90">
                    Carrinhos (Usuários)
                  </div>
                  <div className="text-2xl font-semibold leading-tight">
                    {salesFunnelData
                      ? salesFunnelData.add_to_carts === 0
                        ? renderND(FONTES.ga4, true)
                        : salesFunnelData.add_to_carts.toLocaleString("pt-BR")
                      : funilAusente(FONTES.ga4, true)}
                  </div>
                  <div className="text-xs opacity-70">
                    {salesFunnelData &&
                    typeof salesFunnelData.changes?.add_to_carts === "number"
                      ? variacaoDoFunil(salesFunnelData.changes.add_to_carts)
                      : null}
                  </div>
                </div>

                {/* Fase 3 - Pedidos */}
                <div
                  className="absolute text-center text-white"
                  style={{
                    top: "51%",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  <div className="text-sm font-medium opacity-90">Pedidos</div>
                  <div className="text-2xl font-semibold leading-tight">
                    {salesFunnelData
                      ? salesFunnelData.ecommerce_purchases === 0
                        ? renderND(FONTES.ga4, true)
                        : salesFunnelData.ecommerce_purchases.toLocaleString(
                            "pt-BR",
                          )
                      : funilAusente(FONTES.ga4, true)}
                  </div>
                  <div className="text-xs opacity-70">
                    {salesFunnelData &&
                    typeof salesFunnelData.changes?.ecommerce_purchases ===
                      "number"
                      ? variacaoDoFunil(
                          salesFunnelData.changes.ecommerce_purchases,
                        )
                      : null}
                  </div>
                </div>

                {/* Fase 4 - Receita Total */}
                <div
                  className="absolute text-center text-white"
                  style={{
                    top: "70%",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  <div className="text-sm font-medium opacity-90">
                    Receita Total ({salesFunnelData?.currency_symbol || "R$"})
                  </div>
                  <div className="text-2xl font-semibold leading-tight">
                    {salesFunnelData
                      ? salesFunnelData.total_revenue === 0
                        ? renderND(FONTES.ga4, true)
                        : `${salesFunnelData.currency_symbol} ${salesFunnelData.total_revenue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                      : funilAusente(FONTES.ga4, true)}
                  </div>
                  <div className="text-xs opacity-70">
                    {salesFunnelData &&
                    typeof salesFunnelData.changes?.total_revenue === "number"
                      ? variacaoDoFunil(salesFunnelData.changes.total_revenue)
                      : null}
                  </div>
                </div>

                {/* Fase 5 - ROAS */}
                <div
                  className="absolute text-center text-white"
                  style={{
                    top: "86%",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  <div className="text-sm font-medium opacity-90">
                    ROAS ({salesFunnelData?.currency_symbol || "R$"})
                  </div>
                  <div className="text-2xl font-semibold leading-tight">
                    {salesFunnelData
                      ? salesFunnelData.roas === 0
                        ? renderND(FONTES.ga4ComMidia, true)
                        : Number(salesFunnelData.roas || 0).toLocaleString(
                            "pt-BR",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )
                      : funilAusente(FONTES.ga4ComMidia, true)}
                  </div>
                  <div className="text-xs opacity-70">
                    {salesFunnelData &&
                    typeof salesFunnelData.changes?.roas === "number"
                      ? variacaoDoFunil(salesFunnelData.changes.roas)
                      : null}
                  </div>
                </div>
              </div>
            </div>

            {/* Lateral Direita (col-span-7) */}
            <div className="lg:col-span-7 space-y-3">
              {/* Linha ETAPA 1 */}
              <div className="rounded-md bg-muted/30 border border-border">
                <div className="px-5 py-4">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-medium normal-case">
                    Etapa 1 (Sessões)
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-x-6 gap-y-4 text-sm">
                    <div>
                      <div className="text-muted-foreground text-[11px] flex items-center gap-1">
                        <span>CPC</span>
                        <HelpTooltip
                          content={getKpiTooltip("CPC")}
                          triggerClassName="h-3.5 w-3.5 text-[9px]"
                        />
                      </div>
                      <div className="font-medium text-foreground">
                        {salesFunnelData
                          ? salesFunnelData.cpc === 0
                            ? renderND(FONTES.investimento, false)
                            : `${salesFunnelData.currency_symbol} ${salesFunnelData.cpc.toFixed(2)}`
                          : funilAusente(FONTES.investimento, false)}
                      </div>
                      <div
                        className={`text-[10px] ${salesFunnelData && salesFunnelData.changes.cpc >= 0 ? "text-success-soft" : "text-error-soft"}`}
                      >
                        {salesFunnelData
                          ? variacaoDoFunil(salesFunnelData.changes.cpc)
                          : null}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[11px] flex items-center gap-1">
                        <span>CTR</span>
                        <HelpTooltip
                          content={getKpiTooltip("CTR")}
                          triggerClassName="h-3.5 w-3.5 text-[9px]"
                        />
                      </div>
                      <div className="font-medium text-foreground">
                        {salesFunnelData
                          ? salesFunnelData.ctr === 0
                            ? renderND(FONTES.investimento, false)
                            : salesFunnelData.ctr.toFixed(2) +
                              salesFunnelData.percentage_symbol
                          : funilAusente(FONTES.investimento, false)}
                      </div>
                      <div
                        className={`text-[10px] ${salesFunnelData && salesFunnelData.changes.ctr >= 0 ? "text-success-soft" : "text-error-soft"}`}
                      >
                        {salesFunnelData
                          ? variacaoDoFunil(salesFunnelData.changes.ctr)
                          : null}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[11px] flex items-center gap-1">
                        <span>CPM</span>
                        <HelpTooltip
                          content={getKpiTooltip("CPM")}
                          triggerClassName="h-3.5 w-3.5 text-[9px]"
                        />
                      </div>
                      <div className="font-medium text-foreground">
                        {salesFunnelData
                          ? salesFunnelData.cpm === 0
                            ? renderND(FONTES.investimento, false)
                            : `${salesFunnelData.currency_symbol} ${salesFunnelData.cpm.toFixed(2)}`
                          : funilAusente(FONTES.investimento, false)}
                      </div>
                      <div
                        className={`text-[10px] ${salesFunnelData && salesFunnelData.changes.cpm >= 0 ? "text-success-soft" : "text-error-soft"}`}
                      >
                        {salesFunnelData
                          ? variacaoDoFunil(salesFunnelData.changes.cpm)
                          : null}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[11px] flex items-center gap-1">
                        <span>Investimento</span>
                        <HelpTooltip
                          content={getKpiTooltip("Investimento")}
                          triggerClassName="h-3.5 w-3.5 text-[9px]"
                        />
                      </div>
                      <div className="font-medium text-foreground">
                        {salesFunnelData
                          ? salesFunnelData.investment === 0
                            ? renderND(FONTES.investimento, false)
                            : `${salesFunnelData.currency_symbol} ${salesFunnelData.investment.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                          : funilAusente(FONTES.investimento, false)}
                      </div>
                      <div
                        className={`text-[10px] ${salesFunnelData && salesFunnelData.changes.investment >= 0 ? "text-success-soft" : "text-error-soft"}`}
                      >
                        {salesFunnelData
                          ? variacaoDoFunil(salesFunnelData.changes.investment)
                          : null}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[11px] flex items-center gap-1">
                        <span>Connect Rate</span>
                        <HelpTooltip
                          content={getKpiTooltip("Connect Rate")}
                          triggerClassName="h-3.5 w-3.5 text-[9px]"
                        />
                      </div>
                      <div className="font-medium text-foreground">
                        {salesFunnelData
                          ? salesFunnelData.connect_rate === 0
                            ? renderND(FONTES.ga4ComMidia, false)
                            : salesFunnelData.connect_rate.toFixed(2) +
                              salesFunnelData.percentage_symbol
                          : funilAusente(FONTES.ga4ComMidia, false)}
                      </div>
                      <div
                        className={`text-[10px] ${salesFunnelData && salesFunnelData.changes.connect_rate >= 0 ? "text-success-soft" : "text-error-soft"}`}
                      >
                        {salesFunnelData
                          ? variacaoDoFunil(
                              salesFunnelData.changes.connect_rate,
                            )
                          : null}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[11px] flex items-center gap-1">
                        <span>CPC Google</span>
                        <HelpTooltip
                          content="Custo por clique no Google Ads: investimento do Google ÷ cliques. Útil para comparar eficiência do tráfego do Google."
                          triggerClassName="h-3.5 w-3.5 text-[9px]"
                        />
                      </div>
                      <div className="font-medium text-foreground">
                        {salesFunnelData
                          ? salesFunnelData.cpc_google === 0
                            ? renderND(FONTES.google, false)
                            : `${salesFunnelData.currency_symbol} ${salesFunnelData.cpc_google.toFixed(2)}`
                          : funilAusente(FONTES.google, false)}
                      </div>
                      <div
                        className={`text-[10px] ${salesFunnelData && salesFunnelData.changes.cpc_google >= 0 ? "text-success-soft" : "text-error-soft"}`}
                      >
                        {salesFunnelData
                          ? variacaoDoFunil(salesFunnelData.changes.cpc_google)
                          : null}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[11px] flex items-center gap-1">
                        <span>CTR</span>
                        <HelpTooltip
                          content={getKpiTooltip("CTR")}
                          triggerClassName="h-3.5 w-3.5 text-[9px]"
                        />
                      </div>
                      <div className="font-medium text-foreground">
                        {salesFunnelData
                          ? salesFunnelData.ctr_google === 0
                            ? renderND(FONTES.google, false)
                            : salesFunnelData.ctr_google.toFixed(2) +
                              salesFunnelData.percentage_symbol
                          : funilAusente(FONTES.google, false)}
                      </div>
                      <div
                        className={`text-[10px] ${salesFunnelData && (salesFunnelData.changes.ctr_google ?? 0) >= 0 ? "text-success-soft" : "text-error-soft"}`}
                      >
                        {salesFunnelData
                          ? variacaoDoFunil(
                              salesFunnelData.changes.ctr_google ?? 0,
                            )
                          : null}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[11px] flex items-center gap-1">
                        <span>CPS GERAL</span>
                        <HelpTooltip
                          content={getKpiTooltip("CPS GERAL")}
                          triggerClassName="h-3.5 w-3.5 text-[9px]"
                        />
                      </div>
                      <div className="font-medium text-foreground">
                        {salesFunnelData
                          ? salesFunnelData.cps_geral === 0
                            ? renderND(FONTES.ga4ComMidia, false)
                            : salesFunnelData.cps_geral.toFixed(2)
                          : funilAusente(FONTES.ga4ComMidia, false)}
                      </div>
                      <div
                        className={`text-[10px] ${salesFunnelData && salesFunnelData.changes.cps_geral >= 0 ? "text-success-soft" : "text-error-soft"}`}
                      >
                        {salesFunnelData
                          ? variacaoDoFunil(salesFunnelData.changes.cps_geral)
                          : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Linha ETAPA 2 */}
              <div className="rounded-md bg-muted/30 border border-border">
                <div className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium normal-case">
                      Etapa 2 (Sessões &gt; Carrinhos)
                    </div>
                    <div className="text-[18px] font-medium mt-1">
                      {salesFunnelData
                        ? salesFunnelData.sessions_to_cart_rate === 0
                          ? renderND(FONTES.ga4, false)
                          : salesFunnelData.sessions_to_cart_rate.toFixed(2) +
                            salesFunnelData.percentage_symbol
                        : funilAusente(FONTES.ga4, false)}
                      {salesFunnelData &&
                        salesFunnelData.sessions_to_cart_rate !== 0 && (
                          <span
                            className={`text-[11px] align-middle ml-1 ${
                              salesFunnelData.changes.sessions_to_cart_rate >= 0
                                ? "text-success-soft"
                                : "text-error-soft"
                            }`}
                          >
                            {variacaoDoFunil(
                              salesFunnelData.changes.sessions_to_cart_rate,
                            )}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="text-[13px]">
                    <span className="text-muted-foreground">
                      Sessões &gt; Carrinho:{" "}
                    </span>
                    <span className="font-medium text-foreground">
                      {salesFunnelData
                        ? salesFunnelData.sessions_to_cart_rate === 0
                          ? renderND(FONTES.ga4, false)
                          : salesFunnelData.sessions_to_cart_rate.toFixed(1) +
                            salesFunnelData.percentage_symbol
                        : funilAusente(FONTES.ga4, false)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Linha ETAPA 3 */}
              <div className="rounded-md bg-muted/30 border border-border">
                <div className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium normal-case">
                      Etapa 3 (Carrinhos &gt; Pedidos)
                    </div>
                    <div className="text-[18px] font-medium mt-1">
                      {salesFunnelData
                        ? salesFunnelData.sessions_to_cart_rate === 0 ||
                          salesFunnelData.add_to_carts === 0
                          ? renderND(FONTES.ga4, false)
                          : (
                              (salesFunnelData.ecommerce_purchases /
                                salesFunnelData.add_to_carts) *
                              100
                            ).toFixed(2) + salesFunnelData.percentage_symbol
                        : funilAusente(FONTES.ga4, false)}
                      {salesFunnelData &&
                        salesFunnelData.sessions_to_cart_rate !== 0 &&
                        salesFunnelData.add_to_carts !== 0 && (
                          <span
                            className={`text-[11px] align-middle ml-1 ${
                              (salesFunnelData.ecommerce_purchases /
                                salesFunnelData.add_to_carts) *
                                100 >=
                              (salesFunnelData.changes?.cart_to_purchase_rate ??
                                0)
                                ? "text-success-soft"
                                : "text-error-soft"
                            }`}
                          >
                            {salesFunnelData.changes?.cart_to_purchase_rate !=
                            null
                              ? variacaoDoFunil(
                                  salesFunnelData.changes
                                    .cart_to_purchase_rate ?? 0,
                                )
                              : ""}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="text-[13px]">
                    <span className="text-muted-foreground">
                      Carrinho &gt; Compra:{" "}
                    </span>
                    <span className="font-medium text-foreground">
                      {salesFunnelData
                        ? salesFunnelData.sessions_to_cart_rate === 0 ||
                          salesFunnelData.add_to_carts === 0
                          ? renderND(FONTES.ga4, false)
                          : (
                              (salesFunnelData.ecommerce_purchases /
                                salesFunnelData.add_to_carts) *
                              100
                            ).toFixed(1) + salesFunnelData.percentage_symbol
                        : funilAusente(FONTES.ga4, false)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Linha LEGENDA + REFERÊNCIAS */}
              <div className="grid grid-cols-12 gap-3">
                {/* LEGENDA */}
                <div className="col-span-12 lg:col-span-8">
                  <div className="rounded-md bg-muted/30 border border-border px-4 py-3">
                    <div className="text-xs font-medium mb-1.5 text-foreground">
                      Legenda
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                      <div>
                        •{" "}
                        <span className="font-medium text-foreground">CPS</span>
                        : Custo por sessão;
                      </div>
                      <div>
                        •{" "}
                        <span className="font-medium text-foreground">
                          ROAS
                        </span>
                        : Retorno sobre investimento em marketing;
                      </div>
                      <div>
                        •{" "}
                        <span className="font-medium text-foreground">CPM</span>
                        : Custo por 1.000 impressões;
                      </div>
                      <div>
                        •{" "}
                        <span className="font-medium text-foreground">CTR</span>
                        : Taxa de cliques no link;
                      </div>
                      <div>
                        •{" "}
                        <span className="font-medium text-foreground">CPC</span>
                        : Custo por clique;
                      </div>
                      <div>
                        •{" "}
                        <span className="font-medium text-foreground">CPA</span>
                        : Custo por aquisição (venda).
                      </div>
                    </div>
                  </div>
                </div>

                {/* REFERÊNCIAS */}
                <div className="col-span-12 lg:col-span-4">
                  <div className="rounded-md bg-muted/30 border border-border px-4 py-3">
                    <div className="text-xs font-medium mb-1.5 text-foreground">
                      Referências
                    </div>
                    <div className="text-[11px] space-y-0.5">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">CPS Ruim:</span>{" "}
                        <span className="text-foreground font-medium">
                          R$ 1,00 +
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          CPS Médio:
                        </span>{" "}
                        <span className="text-foreground font-medium">
                          R$ 0,70 +
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">CPS Bom:</span>{" "}
                        <span className="text-foreground font-medium">
                          R$ 0,70 -
                        </span>
                      </div>
                      <div className="flex justify-between mt-1.5">
                        <span className="text-muted-foreground">CPM Ruim:</span>{" "}
                        <span className="text-foreground font-medium">
                          R$ 12,00 +
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          CPM Médio:
                        </span>{" "}
                        <span className="text-foreground font-medium">
                          R$ 8,00 +
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">CPM Bom:</span>{" "}
                        <span className="text-foreground font-medium">
                          R$ 8,00 -
                        </span>
                      </div>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-2">
                      Somente campanhas de conversão e catálogo.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TERCEIRA LINHA: CONTROLE DIÁRIO */}
      <ControleDiarioTable />

      {/* QUARTA LINHA: META ADS E GOOGLE ADS ORIGEM */}
      <AnaliseOrigemTable />

      {/* QUINTA LINHA: CATEGORIAS E PRODUTOS MAIS VENDIDOS */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* <M-212> Sem `drilldownEnabled`: o padrão é `false`, e nenhuma das seis rotas
            desta página aplica `filter_category` ou `filter_product`. */}
        <CategoriasMaisVendidasTable compact />
        <ProdutosMaisVendidosTable compact />
      </div>

      {/* SEXTA LINHA: VENDAS POR DISPOSITIVOS E VENDAS POR ESTADO */}
      <div className="grid gap-4 md:grid-cols-2">
        <VendasDispositivosCard />
        <VendasEstadoCard />
      </div>

      {/* SÉTIMA LINHA: PESQUISA INTERNA */}
      <PesquisaInternaTable />
    </div>
  );
};

// Hierarquia de dimensões para a página Visão Geral
//
// <M-212> As oito dimensões de item saíram junto com o clique que as produzia. Das seis rotas
// desta página, nenhuma aplica `filter_category` ou `filter_product` —
// `/top-category-sales` lê só `filter_device` e `filter_date`, e `/top-selling-products` não
// lê nenhum dos treze —, então o chip afirmava um recorte que não chegava a lugar nenhum.
// Ver **D-054**.
const OVERVIEW_HIERARCHIES: DrillDimension[][] = [
  ["region", "city"],
  ["device"],
  ["search_term"],
  ["date"],
  ["ad_platform"],
];

// Componente wrapper com DrilldownProvider
const NewUnifiedOverview: React.FC = () => {
  return (
    <DrilldownProvider hierarchies={OVERVIEW_HIERARCHIES}>
      <UnifiedOverviewContent />
    </DrilldownProvider>
  );
};

export default NewUnifiedOverview;
